import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useOrganization, useUserProfile, useDeleteOrganization, useUserOrganizations, useSwitchOrganization } from '@/hooks/useUserProfile';
import { useUserRole } from '@/hooks/useUserRole';
import { Building2, User, Shield, Trash2, AlertTriangle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

export default function Settings() {
  const navigate = useNavigate();
  const { data: profile, isLoading: profileLoading } = useUserProfile();
  const { data: organization, isLoading: orgLoading } = useOrganization();
  const { data: role, isLoading: roleLoading } = useUserRole();
  const { data: userOrgs = [] } = useUserOrganizations();
  const deleteOrg = useDeleteOrganization();
  const switchOrg = useSwitchOrganization();
  
  const [confirmName, setConfirmName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const isLoading = profileLoading || orgLoading || roleLoading;
  const isAdmin = role?.role === 'superadmin' || role?.role === 'orgadmin';

  const roleLabels: Record<string, string> = {
    superadmin: 'Super Admin',
    orgadmin: 'Administrateur',
    staff: 'Collaborateur',
    readonly: 'Lecture seule',
  };

  const handleDeleteOrganization = async () => {
    if (!organization?.id) return;
    
    if (confirmName !== organization.name) {
      toast.error('Le nom ne correspond pas');
      return;
    }

    try {
      await deleteOrg.mutateAsync(organization.id);
      setDeleteDialogOpen(false);
      setConfirmName('');
      
      // Check if user has other organizations
      const otherOrgs = userOrgs.filter((uo: any) => uo.organization_id !== organization.id);
      if (otherOrgs.length > 0) {
        // Switch to another organization
        const nextOrg = otherOrgs[0];
        await switchOrg.mutateAsync(nextOrg.organization_id);
        navigate('/dashboard');
      } else {
        // Redirect to onboarding to create new org
        navigate('/onboarding');
      }
    } catch (error) {
      // Error is already handled by the mutation
    }
  };

  return (
    <AppLayout title="Paramètres" description="Configuration de votre espace">
      <PageHeader title="Paramètres" />
      
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Organization Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Organisation</CardTitle>
            </div>
            <CardDescription>Informations de votre organisation</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-32" />
              </div>
            ) : (
              <div className="space-y-2">
                <p className="font-medium">{organization?.name || 'Aucune organisation'}</p>
                {organization?.billing_email && (
                  <p className="text-sm text-muted-foreground">{organization.billing_email}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Profile Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Profil</CardTitle>
            </div>
            <CardDescription>Vos informations personnelles</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-48" />
              </div>
            ) : (
              <div className="space-y-2">
                <p className="font-medium">
                  {profile?.first_name} {profile?.last_name}
                </p>
                <p className="text-sm text-muted-foreground">{profile?.email}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Role Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Rôle</CardTitle>
            </div>
            <CardDescription>Vos permissions dans l'application</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-5 w-32" />
            ) : (
              <p className="font-medium">
                {role?.role ? roleLabels[role.role] || role.role : 'Aucun rôle attribué'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Danger Zone */}
      {isAdmin && organization && (
        <div className="mt-8">
          <Card className="border-destructive/50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <CardTitle className="text-lg text-destructive">Zone de danger</CardTitle>
              </div>
              <CardDescription>Actions irréversibles sur votre organisation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Supprimer l'organisation</p>
                  <p className="text-sm text-muted-foreground">
                    Supprime définitivement l'organisation et toutes ses données (clients, SCI, biens, contrats, locataires, quittances, etc.)
                  </p>
                </div>
                <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="gap-2">
                      <Trash2 className="h-4 w-4" />
                      Supprimer
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                        <AlertTriangle className="h-5 w-5" />
                        Supprimer l'organisation
                      </AlertDialogTitle>
                      <AlertDialogDescription className="space-y-4">
                        <p>
                          Cette action est <strong>irréversible</strong>. Toutes les données associées à l'organisation <strong>"{organization.name}"</strong> seront définitivement supprimées :
                        </p>
                        <ul className="list-disc list-inside text-sm space-y-1">
                          <li>Tous les clients</li>
                          <li>Toutes les SCI</li>
                          <li>Tous les biens immobiliers</li>
                          <li>Tous les contrats de location</li>
                          <li>Tous les locataires</li>
                          <li>Toutes les quittances et appels de fonds</li>
                          <li>Tous les paiements</li>
                          <li>Tous les membres de l'équipe</li>
                        </ul>
                        <div className="pt-4">
                          <Label htmlFor="confirm-name">
                            Pour confirmer, tapez le nom de l'organisation : <strong>{organization.name}</strong>
                          </Label>
                          <Input
                            id="confirm-name"
                            className="mt-2"
                            placeholder={organization.name}
                            value={confirmName}
                            onChange={(e) => setConfirmName(e.target.value)}
                          />
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={() => setConfirmName('')}>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteOrganization}
                        disabled={confirmName !== organization.name || deleteOrg.isPending}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {deleteOrg.isPending ? 'Suppression...' : 'Supprimer définitivement'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AppLayout>
  );
}
